import { AlertPrimary } from '@/components/AlertPrimary';
import { LargeSelect } from '@/components/LargeSelect';
import { RoundedTab } from '@/components/RoundedTab';
import Tabs from '@/components/Tabs';
import { PATTERN_NAME } from '@/utils';
import { Form, Input, Modal } from '@arco-design/web-react';
import { produce } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AddedPods } from '.';
import { useContainerStore } from '../../container';
import { WorkloadComponent } from '../../types';
import {
  isVolumeStorageItem,
  usePersistentVolumeClaims,
  VolumeItem,
  VolumeMountItem,
  VolumeMountTab,
  VolumeMountType,
  VolumeStorageItem,
} from '../_hooks';
import { VolumeOptionLabel } from './volume-option-label';

const FormItem = Form.Item;
const TabPane = Tabs.TabPane;

interface AddVolumeModalProps extends WorkloadComponent {
  /** 集群 */
  cluster: string;
  /** 命名空间 */
  namespace: string;
  /** 打开状态 */
  open?: boolean;
  /** 关闭回调 */
  onClose?: () => void;
  /** 确认回调 */
  onOk?: (values: VolumeStorageItem) => void;
  /** 编辑数据 - 用于编辑状态初始化 */
  data?: VolumeItem;
}

export function AddVolumeModal({
  cluster,
  namespace,
  open,
  onClose,
  onOk,
  data,
}: AddVolumeModalProps) {
  const { t } = useTranslation();
  const msg = {
    mount_volume: t('common:mount.volume'),
    persistent_volume: t('common:persistent.volume'),
    temporary_volume: t('common:ephemeral.volume'),
    hostpath_volume: `HostPath ${t('common:volume')}`,
    no_options: t('common:no.available.resources.found'),
    select_volume_claim: t('common:please.select.persistent.volume.claim'),
    select_volume_claim_tip: t(
      'components:mount.persistent.volumes.created.from.persistent.volume.claims.to.containers',
    ),
    mount_path: t('common:mount.path'),
    volume_name_help: t(
      'common:name.can.only.contain.lowercase.letters.numbers.and.hyphens.must.start.and.end.with.a.lowercase.letter.or.number.maximum.63.characters',
    ),
    host_path_alert: t(
      'common:use.hostpath.volumes.to.mount.files.or.directories.from.host.filesystem.to.containers',
    ),
    volume_name: t('common:volume.name'),
    hostpath_path: t('common:host.path'),
    hostpath_path_error_empty: t('common:please.enter.host.path'),
    persistent_volume_claim: t('common:persistent.volume.claim'),
    storage: t('common:capacity'),
    access_modes: t('common:access.mode'),
  };

  const { containersArray } = useContainerStore();

  const editData = isVolumeStorageItem(data) ? data : undefined;
  const initFormValues = useMemo(() => {
    /** 默认/新建的情况: 基于添加的容器列表关联名字 */
    const initVolumeMounts: VolumeMountItem[] = containersArray.map((container) => ({
      containerName: container.containerSetting.containerName,
      mountType: VolumeMountType.NotMount,
      mountPath: '',
    }));
    /** 表单初始状态 */
    const initialValues: VolumeStorageItem = {
      type: VolumeMountTab.PERSISTENCE,
      selected: undefined,
      volumeName: undefined,
      volumeHostPath: undefined,
      volumeMounts: initVolumeMounts,
    };

    // 如果是编辑状态，可以新增容器，所以需要合并容器列表: [已有的, 新增的]
    if (editData) {
      return produce(editData, (draft) => {
        draft.volumeMounts = containersArray.map((container) => {
          const existItem = draft.volumeMounts.find(
            (item) => item.containerName === container.containerSetting.containerName,
          );
          if (existItem) {
            return existItem;
          }
          return {
            containerName: container.containerSetting.containerName,
            mountType: VolumeMountType.NotMount,
            mountPath: '',
          };
        });
      });
    }

    return initialValues;
  }, [containersArray, editData]);

  const [allValues, setAllValues] = useState<Partial<VolumeStorageItem>>(initFormValues);
  const [tab, setTab] = useState<VolumeMountTab>(initFormValues.type);

  const [form] = Form.useForm<VolumeStorageItem>();
  useEffect(() => {
    if (!open) {
      form.resetFields();
      setTab(VolumeMountTab.PERSISTENCE);
    }
    // 编辑状态下，设置表单值
    if (open && editData) {
      form.setFieldsValue(initFormValues);
      setTab(initFormValues.type);
    }
  }, [open, form, editData, initFormValues]);

  const { options } = usePersistentVolumeClaims(cluster, namespace);

  // 持久卷声明的待选项
  const persistenceOptions = options.map((item) => ({
    value: item.value,
    label: (
      <VolumeOptionLabel
        iconfontName="icon-a-chijiujuanmian"
        value={item.value}
        label={msg.persistent_volume_claim}
        extraItems={[
          {
            value: item.storage,
            label: msg.storage,
            frozenWidth: true,
          },
          {
            value: item.accessModes.join(','),
            label: msg.access_modes,
            frozenWidth: true,
          },
        ]}
      />
    ),
  }));

  const onTabChange = (key: VolumeMountTab) => {
    // 1. 获取当前表单值
    const currentValues = form.getFieldsValue();
    // 2. 更新 allValues 状态，合并当前表单值
    setAllValues((prevValues) => ({ ...prevValues, ...currentValues, type: key }));
    // 3. 更新 tab
    setTab(key);
  };

  const handleOk = () => {
    const values = { ...allValues, type: tab };
    onOk?.(values as VolumeStorageItem);
  };

  return (
    <StyledModal visible={open} onCancel={onClose} onOk={handleOk} title={msg.mount_volume}>
      <Form
        form={form}
        layout="vertical"
        initialValues={initFormValues}
        onValuesChange={(_, values) => {
          setAllValues(values as VolumeStorageItem);
        }}
      >
        <RoundedTab activeTab={tab} onChange={onTabChange}>
          <TabPane key={VolumeMountTab.PERSISTENCE} title={msg.persistent_volume}>
            <FormItem field="selected">
              <LargeSelect
                options={persistenceOptions}
                placeholderNode={
                  <VolumeOptionLabel
                    iconfontName="icon-a-chijiujuanmian"
                    value={options.length ? msg.select_volume_claim : msg.no_options}
                    label={msg.select_volume_claim_tip}
                  />
                }
              />
            </FormItem>
          </TabPane>
          <TabPane key={VolumeMountTab.TEMPORARY} title={msg.temporary_volume}>
            <FormItem
              field="volumeName"
              label={msg.volume_name}
              help={msg.volume_name_help}
              required
              rules={[{ match: PATTERN_NAME }]}
              style={{ marginBottom: '1rem' }}
            >
              <Input />
            </FormItem>
          </TabPane>
          <TabPane key={VolumeMountTab.HOST_PATH} title={msg.hostpath_volume}>
            <AlertPrimary content={msg.host_path_alert} />
            <FormItem
              field="volumeName"
              label={msg.volume_name}
              help={msg.volume_name_help}
              required
              rules={[{ match: PATTERN_NAME }]}
              style={{ marginBlock: '1rem' }}
            >
              <Input />
            </FormItem>
            <FormItem
              field="volumeHostPath"
              label={msg.hostpath_path}
              required
              rules={[{ required: true, message: msg.hostpath_path_error_empty }]}
              style={{ marginBlock: '1rem' }}
            >
              <Input />
            </FormItem>
          </TabPane>
        </RoundedTab>
        <AddedPods form={form} />
      </Form>
    </StyledModal>
  );
}

/** ============================ 样式 ==================================== */
const StyledModal = styled(Modal)`
  width: 800px;
  .arco-modal-content {
    min-height: 400px;
    max-height: calc(100vh - 200px);
    overflow-y: auto;
  }
`;
